import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "../api";
import { PageDialog } from "./ReferenceUI";

type Announcement = {
  id: string;
  title: string;
  body: string;
  status: "draft" | "published";
  revision: number;
  recipientCount: number;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
};
type Preview = { announcement: Announcement; eligibleRecipients: number };
const message = (error: unknown) =>
  error instanceof ApiError && error.code === "announcement_changed"
    ? "Черновик изменён в другом окне. Обновите его перед отправкой."
    : error instanceof ApiError && error.code === "announcement_no_recipients"
      ? "Сейчас нет получателей с включёнными уведомлениями команды. Черновик сохранён."
      : "Не удалось выполнить действие. Проверьте соединение и повторите попытку.";

export function AdminAnnouncements() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [editor, setEditor] = useState<{ saved: Announcement | null; id: string } | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const load = useCallback(async (before?: string) => {
    setError("");
    setLoading(true);
    try {
      const result = await api<{ items: Announcement[]; nextCursor: string | null }>(
        `/v1/admin/announcements?limit=20${before ? `&before=${encodeURIComponent(before)}` : ""}`,
      );
      setItems((current) => (before ? [...current, ...result.items] : result.items));
      setCursor(result.nextCursor);
    } catch {
      setError("Не удалось загрузить историю объявлений.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const dirty = editor && (title !== (editor.saved?.title ?? "") || body !== (editor.saved?.body ?? ""));
  const openEditor = (row?: Announcement) => {
    setTitle(row?.title ?? "");
    setBody(row?.body ?? "");
    setEditor({ saved: row ?? null, id: row?.id ?? crypto.randomUUID() });
    setDialogError("");
    setStatus("");
  };
  const save = async () => {
    if (!editor || busy || !title.trim() || !body.trim()) return;
    setBusy(true);
    setDialogError("");
    try {
      const result = await api<{ announcement: Announcement }>(
        editor.saved ? `/v1/admin/announcements/${editor.id}` : "/v1/admin/announcements",
        {
          method: editor.saved ? "PUT" : "POST",
          body: JSON.stringify({
            title: title.trim(),
            body: body.trim(),
            ...(editor.saved ? { revision: editor.saved.revision } : { id: editor.id }),
          }),
        },
      );
      setEditor({ saved: result.announcement, id: editor.id });
      setTitle(result.announcement.title);
      setBody(result.announcement.body);
      setStatus("Черновик сохранён.");
      await load();
    } catch (error) {
      if (error instanceof ApiError && error.code === "announcement_id_conflict") {
        try {
          const current = await api<Preview>(`/v1/admin/announcements/${editor.id}/preview`);
          setEditor({ saved: current.announcement, id: editor.id });
          setDialogError(
            "Ранее сохранённый черновик найден. Ваш текст остался в редакторе; сохраните изменения ещё раз.",
          );
        } catch {
          setDialogError(message(error));
        }
      } else setDialogError(message(error));
    } finally {
      setBusy(false);
    }
  };
  const showPreview = async (id: string) => {
    if (busy) return;
    setBusy(true);
    setDialogError("");
    try {
      setPreview(await api<Preview>(`/v1/admin/announcements/${id}/preview`));
    } catch (error) {
      if (editor || preview) setDialogError(message(error));
      else setError(message(error));
    } finally {
      setBusy(false);
    }
  };
  const publish = async () => {
    if (!preview || busy) return;
    setBusy(true);
    setDialogError("");
    try {
      const result = await api<{ announcement: Announcement; alreadyPublished: boolean }>(
        `/v1/admin/announcements/${preview.announcement.id}/publish`,
        {
          method: "POST",
          body: JSON.stringify({ revision: preview.announcement.revision, confirmation: "send_announcement" }),
        },
      );
      setPreview(null);
      setEditor(null);
      setStatus(`Объявление отправлено. Получателей: ${result.announcement.recipientCount}.`);
      await load();
    } catch (error) {
      setDialogError(message(error));
    } finally {
      setBusy(false);
    }
  };
  const discard = async () => {
    if (!editor?.saved || busy) return;
    setBusy(true);
    setDialogError("");
    try {
      await api(`/v1/admin/announcements/${editor.id}`, {
        method: "DELETE",
        body: JSON.stringify({ revision: editor.saved.revision }),
      });
      setConfirmDiscard(false);
      setEditor(null);
      setStatus("Черновик удалён.");
      await load();
    } catch (error) {
      setDialogError(message(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="admin-announcements">
      <p className="reference-note">
        Объявления появятся в колокольчике у активных пользователей, которые включили «Команда DonDate».
      </p>
      <button type="button" className="reference-primary" disabled={busy} onClick={() => openEditor()}>
        Новое объявление
      </button>
      {status && !editor && (
        <p className="reference-note" role="status">
          {status}
        </p>
      )}
      {items.map((row) => (
        <button
          type="button"
          key={row.id}
          className="announcement-history-row"
          disabled={busy}
          onClick={() => (row.status === "draft" ? openEditor(row) : void showPreview(row.id))}
        >
          <strong>{row.title}</strong>
          <span>{row.status === "draft" ? "Черновик" : `Отправлено: ${row.recipientCount}`}</span>
          <time dateTime={row.publishedAt ?? row.createdAt}>
            {new Date(row.publishedAt ?? row.createdAt).toLocaleString("ru-RU")}
          </time>
        </button>
      ))}
      {loading && <p role="status">Загружаем объявления…</p>}
      {!loading && !items.length && !error && <p className="reference-note">Объявлений пока нет.</p>}
      {error && (
        <p className="reference-error" role="alert">
          {error}
        </p>
      )}
      {cursor && (
        <button type="button" className="reference-row" disabled={loading || busy} onClick={() => void load(cursor)}>
          Показать ещё объявления
        </button>
      )}
      <button type="button" className="reference-row" disabled={loading || busy} onClick={() => void load()}>
        Обновить историю объявлений
      </button>
      {editor && (
        <PageDialog
          title="Черновик объявления"
          busy={busy}
          onClose={() => {
            if (dirty) setConfirmClose(true);
            else setEditor(null);
          }}
        >
          <label className="reference-field">
            Заголовок
            <input
              className="reference-input"
              value={title}
              maxLength={100}
              disabled={busy}
              onChange={(e) => {
                setTitle(e.target.value);
                setStatus("");
              }}
            />
            <small className="reference-counter">{title.length}/100</small>
          </label>
          <label className="reference-field">
            Текст объявления
            <textarea
              className="reference-input"
              value={body}
              maxLength={2000}
              rows={8}
              disabled={busy}
              onChange={(e) => {
                setBody(e.target.value);
                setStatus("");
              }}
            />
            <small className="reference-counter">{body.length}/2000</small>
          </label>
          {status && (
            <p className="reference-note" role="status">
              {status}
            </p>
          )}
          {dialogError && !preview && !confirmDiscard && (
            <p className="reference-error" role="alert">
              {dialogError}
            </p>
          )}
          <button
            type="button"
            className="reference-primary"
            disabled={busy || !title.trim() || !body.trim() || (!!editor.saved && !dirty)}
            onClick={() => void save()}
          >
            Сохранить черновик
          </button>
          <button
            type="button"
            className="reference-row"
            disabled={busy || !editor.saved || !!dirty}
            onClick={() => void showPreview(editor.id)}
          >
            Предпросмотр и отправка
          </button>
          {dirty && <p className="reference-note">Сохраните изменения, чтобы проверить текст перед отправкой.</p>}
          {editor.saved && (
            <button type="button" className="reference-row" disabled={busy} onClick={() => setConfirmDiscard(true)}>
              Удалить черновик
            </button>
          )}
        </PageDialog>
      )}
      {preview && (
        <PageDialog
          title={preview.announcement.status === "published" ? "Отправленное объявление" : "Предпросмотр объявления"}
          busy={busy}
          onClose={() => {
            setPreview(null);
            setDialogError("");
          }}
        >
          <article className="announcement-preview">
            <span className="reference-note">Команда DonDate</span>
            <h2>{preview.announcement.title}</h2>
            <p>{preview.announcement.body}</p>
          </article>
          {preview.announcement.status === "draft" ? (
            <>
              <p className="reference-note">
                Сейчас подходящих получателей: {preview.eligibleRecipients}. Перед отправкой согласие и статус
                проверяются снова. После отправки текст изменить нельзя.
              </p>
              {dialogError && (
                <p className="reference-error" role="alert">
                  {dialogError}
                </p>
              )}
              <button
                type="button"
                className="reference-primary"
                disabled={busy || preview.eligibleRecipients === 0}
                onClick={() => void publish()}
              >
                {busy ? "Отправляем…" : "Отправить объявление"}
              </button>
              <button
                type="button"
                className="reference-row"
                disabled={busy}
                onClick={() => void showPreview(preview.announcement.id)}
              >
                Обновить предпросмотр
              </button>
            </>
          ) : (
            <p className="reference-note">
              Отправлено{" "}
              {new Date(preview.announcement.publishedAt ?? preview.announcement.createdAt).toLocaleString("ru-RU")}.
              Получателей: {preview.announcement.recipientCount}.
            </p>
          )}
        </PageDialog>
      )}
      {confirmDiscard && (
        <PageDialog title="Удалить черновик?" busy={busy} onClose={() => setConfirmDiscard(false)}>
          <p>Объявление не будет отправлено.</p>
          {dialogError && (
            <p className="reference-error" role="alert">
              {dialogError}
            </p>
          )}
          <button type="button" className="reference-primary" disabled={busy} onClick={() => void discard()}>
            Удалить черновик
          </button>
          <button type="button" className="reference-row" disabled={busy} onClick={() => setConfirmDiscard(false)}>
            Оставить черновик
          </button>
        </PageDialog>
      )}
      {confirmClose && (
        <PageDialog title="Выйти без сохранения?" onClose={() => setConfirmClose(false)}>
          <p>Несохранённые изменения будут потеряны.</p>
          <button type="button" className="reference-row" onClick={() => setConfirmClose(false)}>
            Продолжить редактирование
          </button>
          <button
            type="button"
            className="reference-row"
            onClick={() => {
              setConfirmClose(false);
              setEditor(null);
            }}
          >
            Выйти без сохранения
          </button>
        </PageDialog>
      )}
    </section>
  );
}
